import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type RecordSourceResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Where this record came from (F-079), on the lead, the contact and the deal.
 *
 * The parameters are shown by their URL names and with their values exactly as
 * they arrived - "linkedin " with its space, in a box that shows where the
 * value ends. The UX pointer is specific: marketers debug with the raw strings,
 * and a tidy "LinkedIn" is the one rendering that hides the problem.
 */

type Touch = NonNullable<RecordSourceResult['first']>;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

function TouchBlock({ label, touch, which }: { label: string; touch: Touch; which: string }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const via =
    touch.origin === 'record'
      ? t('source.via.record')
      : touch.formName
        ? t('source.via.form', { values: { form: touch.formName } })
        : t('source.via.formRemoved');
  const rows: Array<{ key: string; label: string; value: string | null }> = [
    ...UTM_KEYS.map((key) => ({ key, label: key, value: touch.utm[key] })),
    { key: 'referrer', label: t('source.referrer'), value: touch.referrer },
    ...touch.extra.map((entry) => ({ key: entry.key, label: entry.key, value: entry.value })),
  ];
  return (
    <div data-touch={which} data-touch-id={touch.touchId} className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-col">
        <SectionLabel>{label}</SectionLabel>
        <span data-touch-when className="text-meta text-ink-meta">
          {formatDateTime(touch.occurredAt, format)} · {via}
        </span>
      </div>
      <dl className="flex flex-col gap-1">
        {rows.map((row) => (
          <div key={row.key} data-touch-param={row.key} className="flex flex-wrap items-baseline gap-2">
            <dt className="font-mono text-meta text-ink-600">{row.label}</dt>
            <dd className="min-w-0">
              {row.value === null ? (
                <span data-touch-absent className="text-meta text-ink-meta">
                  {t('source.notSent')}
                </span>
              ) : (
                // pre, so a leading or trailing space is visible inside the box.
                <code
                  data-touch-value
                  className="whitespace-pre-wrap break-all rounded-chip bg-sunken px-1 font-mono text-dense text-ink"
                >
                  {row.value}
                </code>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function SourcePanel({
  objectKey,
  recordId,
}: {
  objectKey: 'lead' | 'contact' | 'deal';
  recordId: string;
}): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const errorText = useErrorMessage();
  const entitlement = useEntitlement('F-079');

  const source = useQuery<RecordSourceResult, ApiError>({
    queryKey: ['record-source', objectKey, recordId],
    queryFn: () => api.recordSource({ path: { objectKey, recordId } }),
    enabled: entitlement.enabled,
  });

  if (!entitlement.enabled) return null;
  if (source.isPending) return <Skeleton className="h-block-sm w-full" />;
  if (source.isError) {
    return (
      <Panel data-region="source">
        <p data-source-error className="text-dense text-danger-text">
          {errorText(source.error)}
        </p>
      </Panel>
    );
  }

  const { first, last, touchCount, carry } = source.data;
  const same = first !== null && last !== null && first.touchId === last.touchId;

  return (
    <Panel data-region="source">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>{t('source.title')}</SectionLabel>
          {touchCount === null ? null : (
            <span data-touch-count className="text-meta text-ink-meta">
              {t('source.count', { values: { count: formatCount(touchCount, format) } })}
            </span>
          )}
        </div>

        {carry ? (
          carry.carried ? (
            <p data-source-carried className="text-meta text-ink-meta">
              {carry.contactVisible && carry.contactId ? (
                <>
                  {t('source.deal.from')}{' '}
                  <a
                    href={`/contacts/${carry.contactId}`}
                    className="text-bronze-text underline underline-offset-4"
                  >
                    {carry.contactName}
                  </a>
                </>
              ) : (
                <span data-source-contact-hidden>{t('source.deal.hidden')}</span>
              )}
              {carry.carriedAt ? ` · ${formatDateTime(carry.carriedAt, format)}` : null}
            </p>
          ) : (
            <p data-source-none="no-person" className="text-body text-ink-meta">
              {t('source.none.deal')}
            </p>
          )
        ) : null}

        {first === null ? (
          carry && !carry.carried ? null : (
            <p data-source-none={carry ? 'no-touch' : 'none'} className="text-body text-ink-meta">
              {carry ? t('source.deal.noTouch') : t('source.none.person')}
            </p>
          )
        ) : same ? (
          <TouchBlock label={t('source.firstAndLast')} touch={first} which="first-and-last" />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TouchBlock label={t('source.first')} touch={first} which="first" />
            {last ? <TouchBlock label={t('source.last')} touch={last} which="last" /> : null}
          </div>
        )}
      </div>
    </Panel>
  );
}
